import { HEADER_PILL_BOX } from '@/shared/components/pillClasses';

/**
 * How much room a chip takes. Two densities, because a chip appears in two
 * kinds of place and they want different things from it.
 *
 * - `card` is the dense one: a board card carries up to two chips beside a
 *   badge, above a status line and a meta strip, and every pixel it spends is
 *   one the card's actual signals do not get.
 * - `header` is the filter row's, and it is `HEADER_PILL_BOX` itself rather
 *   than a size that happens to look similar. The state pills and the label
 *   pills in that row are required to read as one family (FR-023); when the
 *   chip carried its own `h-5` and 11px text they stood six pixels and one
 *   type step apart, and no constant could enforce a family that only two of
 *   its three members belonged to.
 */
export type LabelChipDensity = 'card' | 'header';

/**
 * The pill itself — everything about a chip except its colour and its padding.
 *
 * Shared rather than repeated because this package has three chips now, and two
 * of them draw the pill: `RemovableLabelChip` needs the same box with a control
 * inside it and therefore different horizontal padding on each side. Two copies
 * of these classes would drift the moment one was adjusted, and the drift would
 * show as two chips of different heights sitting on the same task.
 *
 * Padding is deliberately not in the `card` entry. It is the one value the
 * variants legitimately disagree about; `header` inherits the shared box's,
 * which `RemovableLabelChip` then overrides on one side through `cn`.
 *
 * In its own module rather than beside `LabelChip`, because a file that exports
 * both a component and a constant cannot be hot-reloaded — `react-refresh/only-
 * export-components`, which is an error in this repo.
 */
export const LABEL_CHIP_SHAPE: Record<LabelChipDensity, string> = {
  card: 'inline-flex h-5 w-fit shrink-0 items-center rounded-4xl text-2xs font-medium whitespace-nowrap',
  header: HEADER_PILL_BOX,
};
